/* ColourMatch Pro · Block F — Admin Painter Detail.
 *
 * Premium-CRM single-painter detail page. Establishes the visual standard for
 * every future ColourMatch admin screen. Section order (locked with founder):
 *
 *   00 · Hero banner (identity)
 *   01 · Identity card
 *   02 · Performance (9 KPI tiles — 7 live + 2 "Coming soon")
 *   03 · Gallery (featured-image-first, moderation grid)
 *   04 · Latest customer project (NEW)
 *   05 · Verification
 *   06 · Business health (NEW)
 *   07 · Administration (Actions + Internal Notes)
 *   08 · Audit timeline
 *
 * Design tokens come from the existing brand system. No new colours.
 * NO data tables anywhere. Cards everywhere.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ChevronDown, ChevronUp, ShieldCheck, MapPin, Paintbrush2,
  Star, Award, FileText, AlertCircle, Eye, EyeOff, Trash2, CheckCircle2,
  Clock, Sparkles, Plus, RefreshCw, History, ArrowRight, Hourglass,
  Mail, Phone, Globe, BadgeCheck, ImageIcon,
} from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function absolute(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}

function formatDate(iso, withTime = false) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-AU",
      { year: "numeric", month: "short", day: "numeric" });
    if (!withTime) return date;
    const time = d.toLocaleTimeString("en-AU",
      { hour: "2-digit", minute: "2-digit" });
    return `${date} · ${time}`;
  } catch { return iso; }
}

function relative(iso) {
  if (!iso) return "—";
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return formatDate(iso);
  } catch { return iso; }
}

const HEALTH_TONE = {
  good: { ring: "ring-brand-success/25", text: "text-brand-success", bg: "bg-brand-success/10", chip: "bg-brand-success/15 text-brand-success" },
  ok: { ring: "ring-amber-200", text: "text-amber-700", bg: "bg-amber-50", chip: "bg-amber-100 text-amber-700" },
  warn: { ring: "ring-red-200", text: "text-red-700", bg: "bg-red-50", chip: "bg-red-100 text-red-700" },
  neutral: { ring: "ring-stone-200", text: "text-stone-600", bg: "bg-stone-50", chip: "bg-stone-100 text-stone-700" },
};

function SectionHeader({ index, label, title, subtitle, action }) {
  return (
    <div className="mb-10 flex items-end justify-between gap-6 flex-wrap">
      <div>
        <div className="flex items-center gap-3 text-brand-muted">
          <span className="font-serif text-2xl text-brand-ink/40">
            {String(index).padStart(2, "0")}
          </span>
          <span className="overline tracking-[0.3em]">{label}</span>
        </div>
        <h2 className="mt-3 text-3xl lg:text-4xl font-serif text-brand-ink tracking-tight">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-2 text-brand-muted max-w-2xl">{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  );
}

function KPIcard({ value, label, trend, tone = "ink", comingSoon }) {
  return (
    <div
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`bg-white rounded-[28px] p-8 lg:p-10 transition-all duration-300 hover:-translate-y-1 ${
        comingSoon
          ? "border border-dashed border-brand-border/60 bg-stone-50/60"
          : "border border-brand-border/40 shadow-[0_4px_24px_rgba(12,94,74,0.04)]"
      }`}
    >
      {comingSoon ? (
        <Hourglass size={20} className="text-brand-muted/60" />
      ) : null}
      <div className={`mt-4 font-serif tracking-tight ${
        comingSoon ? "text-3xl text-brand-muted/50" : "text-5xl lg:text-6xl text-brand-ink"
      }`}>
        {comingSoon ? "—" : value}
      </div>
      <div className="mt-3 overline">{label}</div>
      {trend && !comingSoon && (
        <div className="mt-3 text-xs text-brand-success font-medium">{trend}</div>
      )}
      {comingSoon && (
        <div className="mt-3 text-xs text-brand-muted">Coming soon</div>
      )}
    </div>
  );
}

function StatusPill({ status, published }) {
  const map = {
    approved: published
      ? { bg: "bg-brand-success/10", text: "text-brand-success", ring: "ring-brand-success/25", label: "Published" }
      : { bg: "bg-stone-50", text: "text-stone-700", ring: "ring-stone-200", label: "Approved · Unpublished" },
    pending: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", label: "Pending review" },
    suspended: { bg: "bg-red-50", text: "text-red-700", ring: "ring-red-200", label: "Suspended" },
    info_requested: { bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", label: "Info requested" },
    rejected: { bg: "bg-red-50", text: "text-red-700", ring: "ring-red-200", label: "Rejected" },
  };
  const s = map[status] || { bg: "bg-stone-50", text: "text-stone-700", ring: "ring-stone-200", label: status || "Unknown" };
  return (
    <span
      data-testid="painter-status-pill"
      className={`inline-flex items-center gap-1.5 ${s.bg} ${s.text} ring-1 ${s.ring} text-xs font-semibold px-3 py-1.5 rounded-full`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.text.replace("text-", "bg-")}`} />
      {s.label}
    </span>
  );
}

export default function AdminPainterDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");
  const [notes, setNotes] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showAllAreas, setShowAllAreas] = useState(false);
  const [confirm, setConfirm] = useState(null); // {action, fn, body}
  const [savingNote, setSavingNote] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [auditFilter, setAuditFilter] = useState("all");
  const [heroBlobUrl, setHeroBlobUrl] = useState(null);
  const [logoBlobUrl, setLogoBlobUrl] = useState(null);

  // Auth-gated hero + logo. Loaded via axios so admins can see images even
  // when a painter is unpublished/suspended.
  useEffect(() => {
    if (!data?.has_hero_image) return;
    let alive = true; let url = null;
    api.get(`/admin/painters/${id}/hero`, { responseType: "blob" })
      .then((r) => { if (!alive) return; url = URL.createObjectURL(r.data); setHeroBlobUrl(url); })
      .catch(() => setHeroBlobUrl(null));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [id, data?.has_hero_image]);

  useEffect(() => {
    if (!data?.has_logo) return;
    let alive = true; let url = null;
    api.get(`/admin/painters/${id}/logo`, { responseType: "blob" })
      .then((r) => { if (!alive) return; url = URL.createObjectURL(r.data); setLogoBlobUrl(url); })
      .catch(() => setLogoBlobUrl(null));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [id, data?.has_logo]);

  usePageMeta({
    title: data ? `${data.business_name} — Admin · ColourMatch` : "Painter detail — Admin",
    description: "ColourMatch Pro admin painter detail",
    robots: "noindex,nofollow",
  });

  const refresh = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const [d, n, a] = await Promise.all([
        api.get(`/admin/painters/${id}`),
        api.get(`/admin/painters/${id}/notes`),
        api.get(`/admin/painters/${id}/audit?limit=80`),
      ]);
      setData(d.data);
      setNotes(n.data.items || []);
      setAudit(a.data.items || []);
    } catch (e) {
      if (e?.response?.status === 404) {
        setErr("Painter not found.");
      } else if (e?.response?.status === 403 || e?.response?.status === 401) {
        setErr("Admin access required.");
      } else {
        setErr(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  const togglePublish = async () => {
    const next = !data.published;
    try {
      await api.post(`/admin/painters/${id}/${next ? "publish" : "unpublish"}`);
      toast.success(next ? "Painter published" : "Painter unpublished");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const suspendPainter = async (reason) => {
    try {
      await api.post(`/admin/painters/${id}/suspend`, { reason });
      toast.success("Painter suspended");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reinstatePainter = async () => {
    try {
      await api.post(`/admin/painters/${id}/reinstate`);
      toast.success("Painter reinstated");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const galleryAction = async (galleryId, action) => {
    try {
      await api.patch(`/admin/painters/${id}/gallery/${galleryId}`,
        { action });
      toast.success(`Gallery item ${action}d`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addNote = async () => {
    const body = newNote.trim();
    if (!body) return;
    setSavingNote(true);
    try {
      await api.post(`/admin/painters/${id}/notes`, { body });
      setNewNote("");
      toast.success("Note saved");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingNote(false); }
  };

  const filteredAudit = useMemo(() => {
    if (auditFilter === "all") return audit;
    const buckets = {
      status: ["painter.published", "painter.unpublished", "painter.suspended", "painter.reinstated"],
      gallery: ["painter.gallery.approve", "painter.gallery.hide", "painter.gallery.remove"],
      verification: ["painter.cert.added", "painter.cert.removed", "painter.badge.awarded"],
      notes: ["painter.note.added"],
    };
    const allowed = buckets[auditFilter] || [];
    return audit.filter((e) => allowed.includes(e.action));
  }, [audit, auditFilter]);

  if (busy) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center text-brand-muted" data-testid="admin-painter-loading">
        Loading painter detail…
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <AlertCircle size={32} className="mx-auto text-brand-muted mb-4" />
          <h1 className="text-3xl font-serif text-brand-ink">{err || "Painter not found"}</h1>
          <Link to="/admin" data-testid="admin-back-to-list-link"
            className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-teal hover:text-brand-navy">
            ← Back to admin
          </Link>
        </div>
      </div>
    );
  }

  const heroImg = heroBlobUrl;
  const logoImg = logoBlobUrl;
  const perf = data.performance || {};
  const health = data.business_health || { items: [], recommendations: [] };
  const latest = data.latest_customer_project;

  const kpis = [
    { value: perf.leads_total ?? 0, label: "Leads", trend: perf.leads_30d ? `+${perf.leads_30d} this month` : null },
    { value: perf.quotes_responded ?? 0, label: "Quotes Sent" },
    { value: perf.profile_views_total ?? 0, label: "Profile Views", trend: perf.profile_views_30d ? `+${perf.profile_views_30d} this month` : null },
    { value: perf.gallery_views_total ?? 0, label: "Gallery Views", trend: perf.gallery_views_30d ? `+${perf.gallery_views_30d} this month` : null },
    { value: perf.response_rate != null ? `${perf.response_rate}%` : "—", label: "Response Rate" },
    { value: formatDate(data.approved_at), label: "Active Since" },
    { value: formatDate(data.approved_at), label: "Verification Date" },
    { comingSoon: true, label: "Customer Rating" },
    { comingSoon: true, label: "Avg Response Time" },
  ];

  return (
    <div className="bg-brand-bg min-h-screen pb-32" data-testid="admin-painter-detail">
      {/* §00 — Hero banner */}
      <section
        data-testid="admin-painter-hero"
        className="relative bg-brand-ink min-h-[420px] lg:min-h-[560px]"
      >
        {heroImg && (
          <img
            src={heroImg}
            alt={data.business_name}
            className="absolute inset-0 w-full h-full object-cover opacity-80"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10 pt-8 lg:pt-10 pb-12 lg:pb-16 h-full flex flex-col justify-between min-h-[420px] lg:min-h-[560px]">
          {/* Top row: breadcrumb + quick actions */}
          <div className="flex items-start justify-between text-white">
            <Link to="/admin" data-testid="admin-back-to-list-link"
              className="inline-flex items-center gap-2 text-sm text-white/75 hover:text-white">
              <ArrowLeft size={14} /> All painters
            </Link>
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={togglePublish}
                data-testid={data.published ? "admin-unpublish-painter-button-top" : "admin-publish-painter-button-top"}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-4 py-2 rounded-full border border-white/15 transition-colors"
              >
                {data.published ? <EyeOff size={13} /> : <Eye size={13} />}
                {data.published ? "Unpublish" : "Publish"}
              </button>
            </div>
          </div>

          {/* Bottom: identity */}
          <div className="text-white">
            <div className="flex items-center gap-2 flex-wrap mb-5">
              <span className="inline-flex items-center gap-1.5 bg-brand-teal/20 text-brand-teal ring-1 ring-brand-teal/40 text-xs font-semibold px-3 py-1.5 rounded-full">
                <Sparkles size={12} /> Founding Member
              </span>
              {data.verified_since_label && (
                <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm text-white ring-1 ring-white/20 text-xs font-semibold px-3 py-1.5 rounded-full">
                  <ShieldCheck size={12} /> {data.verified_since_label}
                </span>
              )}
              <StatusPill status={data.status} published={data.published} />
            </div>
            <h1
              data-testid="admin-painter-business-name"
              className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02]"
            >
              {data.business_name}
            </h1>
            <div className="mt-4 text-white/80 text-sm flex items-center gap-2">
              <MapPin size={14} /> {data.hq_suburb}, {data.hq_state} {data.hq_postcode}
              <span className="text-white/40 mx-1">·</span>
              <span>/{data.slug}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 space-y-20 lg:space-y-32 mt-20 lg:mt-32">

        {/* §01 — Identity */}
        <section data-testid="admin-section-identity">
          <SectionHeader index={1} label="Identity" title="Who they are" />
          <div className="bg-white rounded-[28px] p-10 lg:p-12 border border-brand-border/40 shadow-[0_4px_24px_rgba(12,94,74,0.04)]">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
              {/* Logo + contact */}
              <div className="lg:col-span-5">
                <div className="flex items-start gap-5">
                  <div className="w-24 h-24 rounded-2xl bg-stone-100 border border-brand-border/40 flex-shrink-0 overflow-hidden">
                    {logoImg ? (
                      <img src={logoImg} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-brand-muted">
                        <Paintbrush2 size={28} />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="overline">Owner</div>
                    <div className="mt-1 text-lg font-serif text-brand-ink">{data.owner_name || "—"}</div>
                  </div>
                </div>

                <div className="mt-8 space-y-3 text-sm">
                  {data.contact_email && (
                    <div className="flex items-center gap-2.5 text-brand-ink">
                      <Mail size={14} className="text-brand-muted" />
                      <a href={`mailto:${data.contact_email}`} className="hover:text-brand-teal">{data.contact_email}</a>
                    </div>
                  )}
                  {data.contact_phone && (
                    <div className="flex items-center gap-2.5 text-brand-ink">
                      <Phone size={14} className="text-brand-muted" />
                      <a href={`tel:${data.contact_phone}`} className="hover:text-brand-teal">{data.contact_phone}</a>
                    </div>
                  )}
                  {data.website_url && (
                    <div className="flex items-center gap-2.5 text-brand-ink">
                      <Globe size={14} className="text-brand-muted" />
                      <a href={data.website_url} target="_blank" rel="noreferrer" className="hover:text-brand-teal">{data.website_url}</a>
                    </div>
                  )}
                  {data.abn && (
                    <div className="flex items-center gap-2.5 text-brand-ink">
                      <BadgeCheck size={14} className="text-brand-muted" />
                      ABN {data.abn}
                    </div>
                  )}
                </div>
              </div>

              {/* Service areas + scope */}
              <div className="lg:col-span-7 lg:border-l lg:border-brand-border/40 lg:pl-12 space-y-7">
                <div>
                  <div className="overline mb-3">Service regions</div>
                  <div className="flex flex-wrap gap-2">
                    {(data.areas_regions || []).length === 0 && (
                      <span className="text-sm text-brand-muted">No regions configured.</span>
                    )}
                    {(data.areas_regions || []).map((r) => (
                      <span key={r} className="inline-flex items-center gap-1.5 bg-brand-success/10 text-brand-success text-sm font-semibold px-3 py-1.5 rounded-full">
                        <MapPin size={12} /> {r}
                      </span>
                    ))}
                  </div>
                  {(data.areas_detail || []).length > 0 && (
                    <button
                      onClick={() => setShowAllAreas((v) => !v)}
                      data-testid="admin-expand-service-areas-button"
                      className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-brand-ink hover:text-brand-teal"
                    >
                      {showAllAreas ? "Hide" : "Show"} all {data.areas_detail.length} postcodes
                      {showAllAreas ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                  )}
                  {showAllAreas && (
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2 text-xs">
                      {data.areas_detail.map((a) => (
                        <div key={a.postcode} className="bg-stone-50 rounded-lg px-2.5 py-1.5 text-brand-ink">
                          <span className="font-semibold">{a.postcode}</span> {a.suburb}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-6 text-sm">
                  <div>
                    <div className="overline mb-2">Scope</div>
                    <div className="text-brand-ink capitalize">{(data.service_scope || []).join(" · ") || "—"}</div>
                  </div>
                  <div>
                    <div className="overline mb-2">Surfaces</div>
                    <div className="text-brand-ink capitalize">{(data.service_surfaces || []).join(" · ").replace(/_/g, " ") || "—"}</div>
                  </div>
                  <div>
                    <div className="overline mb-2">Property</div>
                    <div className="text-brand-ink capitalize">{(data.property_types || []).join(" · ") || "—"}</div>
                  </div>
                  <div>
                    <div className="overline mb-2">Team size</div>
                    <div className="text-brand-ink">{data.team_size || "—"}</div>
                  </div>
                  <div>
                    <div className="overline mb-2">Years in business</div>
                    <div className="text-brand-ink">{data.years_in_business || "—"}</div>
                  </div>
                  <div>
                    <div className="overline mb-2">Plan</div>
                    <div className="text-brand-ink">{data.membership_status}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* §02 — Performance */}
        <section data-testid="admin-section-performance">
          <SectionHeader index={2} label="Performance" title="At a glance" subtitle="Lifetime metrics with 30-day momentum where available." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {kpis.map((k, i) => (
              <KPIcard key={i} {...k} />
            ))}
          </div>
        </section>

        {/* §03 — Gallery */}
        <section data-testid="admin-section-gallery">
          <SectionHeader
            index={3}
            label="Gallery"
            title="The work that earned them their reputation"
            subtitle={`${data.gallery?.length || 0} image${data.gallery?.length === 1 ? "" : "s"} in their public portfolio. Hover any image to moderate.`}
          />
          {(!data.gallery || data.gallery.length === 0) ? (
            <div className="bg-white rounded-[28px] p-16 border border-dashed border-brand-border/40 text-center">
              <ImageIcon size={36} className="mx-auto text-brand-muted/60" />
              <p className="mt-4 text-brand-ink font-medium">No gallery images yet.</p>
              <p className="mt-1 text-sm text-brand-muted">The painter can upload work samples from their dashboard.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Featured (first image) — 16:9 hero */}
              <GalleryCard
                item={data.gallery[0]}
                painterId={data.id}
                featured
                onAction={(action) => galleryAction(data.gallery[0].id, action)}
              />
              {/* Next 2 — 4:3 large pair */}
              {data.gallery.length > 1 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {data.gallery.slice(1, 3).map((g) => (
                    <GalleryCard
                      key={g.id} item={g} painterId={data.id}
                      onAction={(action) => galleryAction(g.id, action)}
                    />
                  ))}
                </div>
              )}
              {/* Remaining — 4-up small grid */}
              {data.gallery.length > 3 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {data.gallery.slice(3).map((g) => (
                    <GalleryCard
                      key={g.id} item={g} painterId={data.id} small
                      onAction={(action) => galleryAction(g.id, action)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* §04 — Latest customer project */}
        <section data-testid="admin-section-latest-project">
          <SectionHeader
            index={4}
            label="Latest customer project"
            title="What homeowners are asking for"
            subtitle="The most recent ColourMatch project sent through to this painter."
          />
          {latest ? (
            <div className="bg-white rounded-[28px] p-10 lg:p-12 border border-brand-border/40 shadow-[0_4px_24px_rgba(12,94,74,0.04)]">
              <div className="grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5">
                  <div className="overline">Customer</div>
                  <div className="mt-2 text-2xl font-serif text-brand-ink">{latest.customer_name || "Anonymous"}</div>
                  {latest.suburb && (
                    <div className="mt-1 text-sm text-brand-muted flex items-center gap-1.5">
                      <MapPin size={12} /> {latest.suburb}
                    </div>
                  )}
                  <div className="mt-6 space-y-3 text-sm">
                    <div className="flex items-center gap-2 text-brand-ink">
                      <span className="overline w-28">Project</span>
                      {latest.project_title || "Untitled"}
                    </div>
                    <div className="flex items-center gap-2 text-brand-ink">
                      <span className="overline w-28">Scope</span>
                      <span className="capitalize">{latest.project_scope || "—"}</span>
                    </div>
                    <div className="flex items-center gap-2 text-brand-ink">
                      <span className="overline w-28">Status</span>
                      <span className="capitalize">{latest.status}</span>
                    </div>
                    <div className="flex items-center gap-2 text-brand-ink">
                      <span className="overline w-28">Received</span>
                      {relative(latest.received_at)}
                    </div>
                  </div>
                  <button
                    onClick={() => nav("/painter/leads")}
                    data-testid="admin-view-lead-button"
                    className="mt-8 inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navy/90 text-white text-sm font-semibold h-11 px-5 rounded-full transition-colors"
                  >
                    View lead in painter dashboard <ArrowRight size={14} />
                  </button>
                </div>
                <div className="lg:col-span-7 lg:border-l lg:border-brand-border/40 lg:pl-10">
                  <div className="overline mb-3">{latest.scheme_name || "Selected colour scheme"}</div>
                  {latest.scheme_colours?.length > 0 ? (
                    <div className="space-y-2.5">
                      {latest.scheme_colours.map((c, i) => (
                        <div key={`pcol-${i}`} className="flex items-center gap-3 text-sm bg-stone-50 rounded-2xl px-4 py-3">
                          <span className="inline-block w-8 h-8 rounded-lg border border-white shadow-sm" style={{ background: c.color_hex }} />
                          <div className="flex-1">
                            <div className="font-medium text-brand-ink">{c.color_name || c.color_hex}</div>
                            <div className="text-xs text-brand-muted capitalize">{[c.surface, c.finish].filter(Boolean).join(" · ").replace(/_/g, " ")}</div>
                          </div>
                          <span className="text-xs font-mono text-brand-muted">{c.color_hex}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-brand-muted text-sm">No colour scheme was attached to this enquiry.</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-[28px] p-16 border border-dashed border-brand-border/40 text-center">
              <Paintbrush2 size={36} className="mx-auto text-brand-muted/60" />
              <p className="mt-4 text-brand-ink font-medium">No customer projects yet</p>
              <p className="mt-1 text-sm text-brand-muted max-w-md mx-auto">
                Once a homeowner sends through a ColourMatch project, the latest one will appear here so the painter (and admins) understand the brief at a glance.
              </p>
            </div>
          )}
        </section>

        {/* §05 — Proposal Activity (placeholder, design-only) */}
        <section data-testid="admin-section-proposal-activity">
          <SectionHeader
            index={5}
            label="Proposal activity"
            title="What they're winning"
            subtitle="Live insight into the painter's proposal funnel — coming with the Painter Proposal Builder."
          />
          <div className="bg-white rounded-[28px] p-10 lg:p-12 border border-brand-border/40 shadow-[0_4px_24px_rgba(12,94,74,0.04)]">
            <div className="flex items-center gap-2 mb-8">
              <Hourglass size={16} className="text-brand-muted/60" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-brand-muted font-semibold">Coming soon</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
              <KPIcard comingSoon label="Proposals Created" />
              <KPIcard comingSoon label="Proposals Viewed" />
              <KPIcard comingSoon label="Acceptance Rate" />
            </div>
            <p className="mt-8 text-sm text-brand-muted max-w-2xl">
              Once the Painter Proposal Builder ships, this section will show how many proposals this painter has sent, how often customers open them, and what percentage convert into accepted work.
            </p>
          </div>
        </section>

        {/* §06 — Subscription overview (hidden until Stripe goes live) */}
        {data.subscription_visible && data.subscription && (
          <section data-testid="admin-section-subscription">
            <SectionHeader
              index={6}
              label="Subscription"
              title="Plan & billing"
              subtitle="Founding Member, paid plans, renewal date and payment history."
            />
            <div className="bg-white rounded-[28px] p-10 lg:p-12 border border-brand-border/40 shadow-[0_4px_24px_rgba(12,94,74,0.04)]">
              <div className="grid lg:grid-cols-3 gap-8">
                <div>
                  <div className="overline mb-3">Current plan</div>
                  <div className="text-2xl font-serif text-brand-ink">{data.subscription.plan || "—"}</div>
                  <div className="mt-2 text-sm text-brand-muted">{data.subscription.status || "—"}</div>
                </div>
                <div>
                  <div className="overline mb-3">Renews</div>
                  <div className="text-2xl font-serif text-brand-ink">
                    {data.subscription.renewal_at ? formatDate(data.subscription.renewal_at) : "—"}
                  </div>
                </div>
                <div>
                  <div className="overline mb-3">Lifetime spend</div>
                  <div className="text-2xl font-serif text-brand-ink">
                    {data.subscription.lifetime_amount_aud != null ? `A$${data.subscription.lifetime_amount_aud.toFixed(2)}` : "—"}
                  </div>
                </div>
              </div>
              {data.subscription.payment_history?.length > 0 && (
                <div className="mt-10 pt-8 border-t border-brand-border/30">
                  <div className="overline mb-4">Payment history</div>
                  <ul className="divide-y divide-brand-border/30">
                    {data.subscription.payment_history.map((p, i) => (
                      <li key={i} className="py-3 flex items-baseline justify-between text-sm">
                        <span className="text-brand-ink">{formatDate(p.date)}</span>
                        <span className="font-medium text-brand-ink">A${Number(p.amount_aud || 0).toFixed(2)}</span>
                        <span className="text-xs text-brand-muted">{p.method || "—"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>
        )}

        {/* §07 — Verification */}
        <section data-testid="admin-section-verification">
          <SectionHeader index={7} label="Verification" title="Compliance & badges" />
          <div className="bg-white rounded-[28px] border border-brand-border/40 shadow-[0_4px_24px_rgba(12,94,74,0.04)] overflow-hidden">
            <VerificationRow icon={ShieldCheck} title="Licence" value={data.abn_digits ? "On file" : "Not provided"} chip={{ label: data.abn_digits ? "Verified" : "Pending", tone: data.abn_digits ? "good" : "ok" }} />
            <VerificationRow icon={FileText} title="Insurance" value="On file" chip={{ label: "Verified", tone: "good" }} />
            <VerificationRow icon={BadgeCheck} title="ABN" value={data.abn || "—"} chip={{ label: data.abn ? "Verified" : "Pending", tone: data.abn ? "good" : "ok" }} />
            <VerificationRow icon={FileText} title="Certificates" value={`${data.certificates?.length || 0} on file`} chip={{ label: data.certificates?.length ? "Available" : "None", tone: data.certificates?.length ? "good" : "neutral" }} />
            <VerificationRow icon={Award} title="Badges" value={`${data.badges?.length || 0} awarded`} chip={{ label: data.badges?.length ? "Awarded" : "None yet", tone: data.badges?.length ? "good" : "neutral" }} last />
          </div>
        </section>

        {/* §08 — Business Health */}
        <section data-testid="admin-section-business-health">
          <SectionHeader
            index={8}
            label="Business health"
            title="Account at a glance"
            subtitle="A quick health score and ColourMatch's recommendations."
          />
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Overall ring */}
            <div className="lg:col-span-4 bg-white rounded-[28px] p-10 lg:p-12 border border-brand-border/40 shadow-[0_4px_24px_rgba(12,94,74,0.04)] flex flex-col items-center justify-center text-center">
              <div className={`relative w-40 h-40 rounded-full ring-8 ${HEALTH_TONE[health.overall_level]?.ring || "ring-stone-200"} flex items-center justify-center bg-white`}>
                <div className="text-5xl font-serif tracking-tight text-brand-ink">{health.overall_score}</div>
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white px-3 text-[10px] uppercase tracking-[0.2em] text-brand-muted">out of 100</div>
              </div>
              <div className={`mt-6 inline-flex items-center gap-1.5 ${HEALTH_TONE[health.overall_level]?.chip || ""} text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider`}>
                {health.overall_level === "good" ? "Healthy" : health.overall_level === "ok" ? "Needs attention" : "Action required"}
              </div>
              <p className="mt-6 text-sm text-brand-muted max-w-xs">A rolling readout of the painter&rsquo;s profile, gallery, verification &amp; response performance.</p>
            </div>

            {/* Items + recommendations */}
            <div className="lg:col-span-8 space-y-4">
              {(health.items || []).map((it) => (
                <div key={it.key} data-testid={`health-item-${it.key}`} className="bg-white rounded-2xl px-6 py-5 border border-brand-border/40 flex items-center gap-5">
                  <div className={`w-2 h-12 rounded-full ${HEALTH_TONE[it.level]?.text.replace("text-", "bg-") || "bg-stone-300"}`} />
                  <div className="flex-1">
                    <div className="font-semibold text-brand-ink">{it.label}</div>
                    <div className="text-xs text-brand-muted mt-0.5">{it.detail}</div>
                  </div>
                  <div className="text-2xl font-serif text-brand-ink tabular-nums">
                    {it.level === "neutral" ? "—" : `${it.score}`}
                  </div>
                </div>
              ))}

              <div className="bg-brand-ink text-white rounded-[28px] p-8 lg:p-10 mt-6">
                <div className="inline-flex items-center gap-1.5 bg-brand-teal/20 text-brand-teal text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 rounded-full mb-4">
                  <Sparkles size={11} /> ColourMatch recommends
                </div>
                <ul className="space-y-3" data-testid="health-recommendations">
                  {(health.recommendations || []).map((rec, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/90">
                      <CheckCircle2 size={16} className="text-brand-teal flex-shrink-0 mt-0.5" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* §09 — Administration */}
        <section data-testid="admin-section-administration">
          <SectionHeader
            index={9}
            label="Administration"
            title="Manage this painter"
            subtitle="Use these controls sparingly. Every action is logged in the audit timeline below."
          />
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Actions */}
            <div className="lg:col-span-5 bg-white rounded-[28px] p-8 lg:p-10 border border-brand-border/40 shadow-[0_4px_24px_rgba(12,94,74,0.04)]">
              <div className="overline mb-5">Actions</div>
              <div className="space-y-3">
                <button
                  onClick={togglePublish}
                  data-testid={data.published ? "admin-unpublish-painter-button" : "admin-publish-painter-button"}
                  className={`w-full inline-flex items-center justify-between text-sm font-semibold h-12 px-5 rounded-2xl transition-colors ${
                    data.published
                      ? "bg-stone-100 hover:bg-stone-200 text-brand-ink"
                      : "bg-brand-navy hover:bg-brand-navy/90 text-white"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {data.published ? <EyeOff size={15} /> : <Eye size={15} />}
                    {data.published ? "Unpublish from directory" : "Publish to directory"}
                  </span>
                  <ArrowRight size={14} />
                </button>

                {data.status === "suspended" ? (
                  <button
                    onClick={reinstatePainter}
                    data-testid="admin-reinstate-painter-button"
                    className="w-full inline-flex items-center justify-between text-sm font-semibold h-12 px-5 rounded-2xl bg-white hover:bg-stone-50 border border-brand-border text-brand-ink transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <RefreshCw size={15} /> Reinstate painter
                    </span>
                    <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirm({
                      title: "Suspend this painter?",
                      description: "They'll be removed from the public directory and unable to receive new leads until reinstated. Their existing leads will remain intact.",
                      cta: "Suspend painter",
                      tone: "danger",
                      fn: () => suspendPainter("Admin-initiated suspension"),
                    })}
                    data-testid="admin-suspend-painter-button"
                    className="w-full inline-flex items-center justify-between text-sm font-semibold h-12 px-5 rounded-2xl bg-white hover:bg-red-50 border border-brand-border hover:border-red-200 text-brand-ink hover:text-red-700 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <AlertCircle size={15} /> Suspend painter
                    </span>
                    <ArrowRight size={14} />
                  </button>
                )}

                <a
                  href="#audit"
                  data-testid="admin-view-audit-history-button"
                  className="w-full inline-flex items-center justify-between text-sm font-semibold h-12 px-5 rounded-2xl bg-white hover:bg-stone-50 border border-brand-border text-brand-ink transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <History size={15} /> View audit history
                  </span>
                  <ArrowRight size={14} />
                </a>
              </div>
            </div>

            {/* Internal Notes */}
            <div className="lg:col-span-7 bg-white rounded-[28px] p-8 lg:p-10 border border-brand-border/40 shadow-[0_4px_24px_rgba(12,94,74,0.04)]" data-testid="admin-internal-notes">
              <div className="flex items-baseline justify-between mb-5">
                <div className="overline">Internal notes</div>
                <span className="text-xs text-brand-muted">{notes.length} note{notes.length === 1 ? "" : "s"}</span>
              </div>

              <div className="space-y-2.5">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add an internal note about this painter. Never visible to the painter or the public."
                  data-testid="admin-new-note-input"
                  rows={3}
                  className="w-full bg-stone-50 border border-brand-border/60 rounded-2xl px-4 py-3 text-sm text-brand-ink placeholder:text-brand-muted/70 focus:outline-none focus:border-brand-teal focus:bg-white transition-colors resize-none"
                  maxLength={2000}
                />
                <div className="flex justify-end">
                  <button
                    onClick={addNote}
                    disabled={!newNote.trim() || savingNote}
                    data-testid="admin-add-note-button"
                    className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navy/90 text-white text-sm font-semibold h-10 px-5 rounded-full transition-colors disabled:opacity-50"
                  >
                    <Plus size={14} /> {savingNote ? "Saving…" : "Add note"}
                  </button>
                </div>
              </div>

              {notes.length > 0 && (
                <div className="mt-6 pt-6 border-t border-brand-border/30 space-y-4 max-h-72 overflow-y-auto pr-1" data-testid="admin-notes-list">
                  {notes.map((n) => (
                    <div key={n.id} className="text-sm">
                      <div className="text-brand-ink whitespace-pre-wrap">{n.body}</div>
                      <div className="mt-1 text-xs text-brand-muted">{n.author_email || "Admin"} · {relative(n.created_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* §10 — Audit timeline */}
        <section id="audit" data-testid="admin-section-audit">
          <SectionHeader
            index={10}
            label="Audit timeline"
            title="Every administrative action, in one place"
            subtitle="Use the filters below to narrow by category. Newest events first."
          />
          <div className="mb-6 flex flex-wrap gap-2" data-testid="audit-filter-row">
            {[
              ["all", "All"], ["status", "Status"], ["gallery", "Gallery"],
              ["verification", "Verification"], ["notes", "Notes"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setAuditFilter(key)}
                data-testid={`audit-filter-${key}`}
                className={`text-xs font-semibold px-4 py-2 rounded-full transition-colors ${
                  auditFilter === key
                    ? "bg-brand-ink text-white"
                    : "bg-white border border-brand-border text-brand-ink hover:border-brand-teal hover:text-brand-teal"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {filteredAudit.length === 0 ? (
            <div className="bg-white rounded-[28px] p-12 border border-dashed border-brand-border/40 text-center text-brand-muted text-sm">
              No events in this filter yet.
            </div>
          ) : (
            <ol className="relative border-l border-brand-border/40 ml-4 space-y-6" data-testid="audit-timeline">
              {filteredAudit.map((ev) => (
                <li key={ev.id} className="ml-6 relative">
                  <span className="absolute -left-[33px] top-1 w-3 h-3 rounded-full bg-brand-navy ring-4 ring-white" />
                  <div className="bg-white rounded-2xl px-6 py-4 border border-brand-border/40">
                    <div className="flex items-baseline justify-between gap-4 flex-wrap">
                      <div className="text-sm font-semibold text-brand-ink">{ev.action}</div>
                      <div className="text-xs text-brand-muted">{formatDate(ev.ts, true)} · {relative(ev.ts)}</div>
                    </div>
                    <div className="mt-1 text-xs text-brand-muted">{ev.actor_email || "System"}</div>
                    {(ev.after && Object.keys(ev.after).length > 0) && (
                      <pre className="mt-3 text-[11px] bg-stone-50 rounded-lg px-3 py-2 text-brand-ink/80 overflow-x-auto">
                        {JSON.stringify(ev.after, null, 2)}
                      </pre>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>

      {/* Confirmation dialog */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-confirm-confirm"
              onClick={() => { confirm?.fn?.(); setConfirm(null); }}
              className={confirm?.tone === "danger" ? "bg-red-600 hover:bg-red-700" : ""}
            >
              {confirm?.cta || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---- Sub-components ---------------------------------------------------
function GalleryCard({ item, painterId, featured = false, small = false, onAction }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let url = null;
    api.get(`/admin/painters/${painterId}/gallery/${item.id}/image`, { responseType: "blob" })
      .then((r) => {
        if (!alive) return;
        url = URL.createObjectURL(r.data);
        setSrc(url);
      })
      .catch(() => setSrc(null));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [item.id, painterId]);
  const hidden = item.published === false;
  const aspect = featured ? "aspect-[16/9]" : small ? "aspect-[4/3]" : "aspect-[4/3]";
  return (
    <div
      data-testid={`admin-gallery-card-${item.id}`}
      className={`group relative ${aspect} rounded-[24px] overflow-hidden bg-stone-100 border border-brand-border/40`}
    >
      <img
        src={src || ""}
        alt={item.title || ""}
        loading="lazy"
        className={`w-full h-full object-cover transition-transform duration-500 ${hidden ? "opacity-50" : "group-hover:scale-[1.03]"}`}
      />
      {hidden && (
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
          Hidden
        </span>
      )}
      {item.is_cover && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-brand-success text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
          <Star size={10} /> Cover
        </span>
      )}
      {/* Hover overlay with actions */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5 lg:p-6">
        <div className="text-white">
          {item.title && <div className="font-medium text-sm lg:text-base">{item.title}</div>}
          {item.caption && !small && <div className="text-xs text-white/80 mt-0.5 line-clamp-1">{item.caption}</div>}
        </div>
        <div className={`mt-3 flex items-center gap-2 ${small ? "flex-col items-stretch" : ""}`}>
          <button
            onClick={() => onAction(hidden ? "approve" : "approve")}
            disabled={!hidden && item.published !== false}
            data-testid={`admin-approve-gallery-image-button-${item.id}`}
            className="inline-flex items-center justify-center gap-1.5 bg-brand-success/90 hover:bg-brand-success text-white text-xs font-semibold px-3 py-2 rounded-full transition-colors disabled:opacity-50"
            title="Approve"
          >
            <CheckCircle2 size={12} /> Approve
          </button>
          <button
            onClick={() => onAction("hide")}
            disabled={hidden}
            data-testid={`admin-hide-gallery-image-button-${item.id}`}
            className="inline-flex items-center justify-center gap-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white text-xs font-semibold px-3 py-2 rounded-full transition-colors disabled:opacity-50"
            title="Hide"
          >
            <EyeOff size={12} /> Hide
          </button>
          <button
            onClick={() => onAction("remove")}
            data-testid={`admin-remove-gallery-image-button-${item.id}`}
            className="inline-flex items-center justify-center gap-1.5 bg-red-500/85 hover:bg-red-500 text-white text-xs font-semibold px-3 py-2 rounded-full transition-colors"
            title="Remove"
          >
            <Trash2 size={12} /> Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function VerificationRow({ icon: Icon, title, value, chip, last }) {
  const tone = HEALTH_TONE[chip?.tone] || HEALTH_TONE.neutral;
  return (
    <div className={`flex items-center justify-between gap-6 px-8 lg:px-10 py-6 ${last ? "" : "border-b border-brand-border/30"}`}>
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-stone-50 flex items-center justify-center text-brand-ink">
          <Icon size={18} />
        </div>
        <div>
          <div className="font-semibold text-brand-ink">{title}</div>
          <div className="text-sm text-brand-muted">{value}</div>
        </div>
      </div>
      {chip && (
        <span className={`${tone.chip} text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full`}>
          {chip.label}
        </span>
      )}
    </div>
  );
}
